import { useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { User, Bell, Shield, Clock, RefreshCw, UserPlus, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { useSettingsStore, SessionTimeout } from '@store/settingsStore';
import { useAuthStore } from '@store/authStore';
import { useTeamOperations } from '@hooks/useTeam';
import { getAccessToken } from '@lib/msal';

export default function Settings() {
  const { accounts } = useMsal();
  const account = accounts[0];
  const [isSyncingAvatar, setIsSyncingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  // Team bootstrap state
  const { teamStatus, teamRole, setTeamMembership } = useAuthStore();
  const { bootstrap } = useTeamOperations();
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleBootstrap = async () => {
    if (!account?.name || !account?.username) return;

    setIsBootstrapping(true);
    setBootstrapResult(null);

    try {
      await bootstrap();
      setBootstrapResult({ success: true, message: 'You are now set up as the first admin!' });
      // Update auth store to reflect new status
      setTeamMembership('member', 'Admin', undefined);
      // Reload the page after a short delay to refresh team status
      setTimeout(() => window.location.reload(), 1500);
    } catch (err) {
      setBootstrapResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to set up admin access',
      });
    } finally {
      setIsBootstrapping(false);
    }
  };

  // Show bootstrap option only if explicitly not in team
  // Don't show if teamStatus is 'member' or 'checking', or if bootstrap already failed with "already has members"
  const alreadyHasMembers = bootstrapResult?.message?.includes('already has members');
  const showBootstrapOption = !alreadyHasMembers &&
    (teamStatus === 'not_in_team' || (teamStatus === 'unknown' && !teamRole));

  const {
    sessionTimeout,
    emailNotifications,
    lowStockAlerts,
    avatarUrl,
    setSessionTimeout,
    setEmailNotifications,
    setLowStockAlerts,
    setAvatar,
  } = useSettingsStore();

  const initials = account?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'U';

  const handleSyncAvatar = async () => {
    if (!account?.username) return;

    setIsSyncingAvatar(true);
    setAvatarError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setAvatarError('Not authenticated. Please sign in again.');
        return;
      }
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${apiUrl}/beacon/avatar?email=${encodeURIComponent(account.username)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();

      if (data.avatarUrl) {
        setAvatar(data.avatarUrl, data.personId);
      } else {
        setAvatarError('No avatar found in Beacon CRM for your email address.');
      }
    } catch {
      setAvatarError('Failed to fetch avatar from Beacon CRM.');
    } finally {
      setIsSyncingAvatar(false);
    }
  };

  const timeoutOptions: { value: SessionTimeout; label: string }[] = [
    { value: 15, label: '15 minutes' },
    { value: 30, label: '30 minutes' },
    { value: 60, label: '1 hour' },
    { value: 120, label: '2 hours' },
  ];

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Team Setup Bootstrap (only shown when not in team) */}
      {showBootstrapOption && (
        <div className="card border-2 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-3 mb-4">
            <UserPlus className="h-5 w-5 text-amber-600" />
            <h2 className="text-lg font-semibold text-amber-900">Team Setup Required</h2>
          </div>
          <p className="text-sm text-amber-800 mb-4">
            No team members have been set up yet. If you are the first administrator for this system,
            click the button below to set yourself up as the initial admin.
          </p>
          {bootstrapResult && (
            <div className={`flex items-center gap-2 p-3 rounded-lg mb-4 ${
              bootstrapResult.success
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {bootstrapResult.success ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              <span className="text-sm">{bootstrapResult.message}</span>
            </div>
          )}
          <button
            onClick={handleBootstrap}
            disabled={isBootstrapping || bootstrapResult?.success}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isBootstrapping ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Setting up...
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4" />
                Set me up as first admin
              </>
            )}
          </button>
        </div>
      )}

      {/* Profile section */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <User className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
        </div>
        <div className="space-y-4">
          {/* Avatar */}
          <div className="flex items-center gap-4">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={account?.name || 'User'}
                className="h-16 w-16 rounded-full object-cover"
              />
            ) : (
              <div className="h-16 w-16 rounded-full bg-sca-purple-500 flex items-center justify-center text-white font-medium text-xl">
                {initials}
              </div>
            )}
            <div>
              <button
                onClick={handleSyncAvatar}
                disabled={isSyncingAvatar}
                className="btn-secondary text-sm py-1.5"
              >
                {isSyncingAvatar ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-1.5" />
                    {avatarUrl ? 'Update from Beacon' : 'Sync from Beacon'}
                  </>
                )}
              </button>
              {avatarError && (
                <p className="text-sm text-red-600 mt-1">{avatarError}</p>
              )}
            </div>
          </div>

          <div>
            <label className="label">Name</label>
            <input
              type="text"
              value={account?.name || ''}
              disabled
              className="input bg-gray-50"
            />
          </div>
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={account?.username || ''}
              disabled
              className="input bg-gray-50"
            />
          </div>
          <p className="text-sm text-gray-500">
            Profile information is managed through Microsoft Entra ID.
          </p>
        </div>
      </div>

      {/* Session Timeout section */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Clock className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Session Timeout</h2>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label">Auto-logout after inactivity</label>
            <select
              value={sessionTimeout}
              onChange={(e) => setSessionTimeout(Number(e.target.value) as SessionTimeout)}
              className="input"
            >
              {timeoutOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="text-sm text-gray-500 mt-2">
              You will be automatically signed out after this period of inactivity.
            </p>
          </div>
        </div>
      </div>

      {/* Notifications section */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
        </div>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Email notifications</p>
              <p className="text-sm text-gray-500">Receive email updates about your assignments</p>
            </div>
            <button
              type="button"
              onClick={() => setEmailNotifications(!emailNotifications)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-sca-purple-500 focus:ring-offset-2 ${
                emailNotifications ? 'bg-sca-purple-500' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={emailNotifications}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                  emailNotifications ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Low stock alerts</p>
              <p className="text-sm text-gray-500">Get notified when stock falls below threshold</p>
            </div>
            <button
              type="button"
              onClick={() => setLowStockAlerts(!lowStockAlerts)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-sca-purple-500 focus:ring-offset-2 ${
                lowStockAlerts ? 'bg-sca-purple-500' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={lowStockAlerts}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                  lowStockAlerts ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Security section */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Security</h2>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-900">Authentication</p>
            <p className="text-sm text-gray-500 mt-1">
              You are signed in with Microsoft Entra ID. Multi-factor authentication
              is managed through your organisation&apos;s security policies.
            </p>
          </div>
        </div>
      </div>

      {/* About section */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">About</h2>
        <div className="space-y-2 text-sm text-gray-500">
          <p><strong className="text-gray-700">SCA UK Operations Hub</strong></p>
          <p>Version 0.1.0</p>
          <p className="pt-2">
            Built for Sudden Cardiac Arrest UK to help manage volunteers, resources,
            and operations efficiently.
          </p>
        </div>
      </div>
    </div>
  );
}
